/**
 * monitorClient — tiny transport + token helper for the public Monitor
 * product flow (`/monitor/*`).
 *
 * These screens are used by REAL external clients, signed in with their own
 * per-workspace token (returned by the signup endpoint), NOT the operator /
 * Supabase session that `@/api/http` attaches. So this module talks to the
 * API directly with a `Bearer <api_token>` header read from localStorage —
 * it deliberately does NOT go through `apiFetch`, which would overwrite the
 * Authorization header with the operator/dev bearer.
 *
 * Token storage: localStorage key `gi_monitor_token`. The connect + dashboard
 * screens redirect to `/monitor/start` when it is missing.
 */

import { apiBaseUrl } from '@/api/http';

/** localStorage key holding the client's workspace API token. */
export const MONITOR_TOKEN_KEY = 'gi_monitor_token';
/** Optional companion keys, stored alongside the token for display. */
export const MONITOR_INGEST_URL_KEY = 'gi_monitor_ingest_url';
export const MONITOR_WORKSPACE_KEY = 'gi_monitor_workspace';

/** Read the saved client token, or `null` if the client hasn't signed up. */
export function getMonitorToken(): string | null {
  if (typeof localStorage === 'undefined') return null;
  const v = localStorage.getItem(MONITOR_TOKEN_KEY);
  return v && v.trim() !== '' ? v : null;
}

/** Persist the client token (and companion display values) after signup. */
export function saveMonitorSession(opts: {
  token: string;
  ingestUrl?: string | null;
  workspace?: string | null;
}): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(MONITOR_TOKEN_KEY, opts.token);
  if (opts.ingestUrl) localStorage.setItem(MONITOR_INGEST_URL_KEY, opts.ingestUrl);
  if (opts.workspace) localStorage.setItem(MONITOR_WORKSPACE_KEY, opts.workspace);
}

/** Read the saved ingest URL (shown on the connect screen). */
export function getIngestUrl(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(MONITOR_INGEST_URL_KEY);
}

/** Read the saved workspace name. */
export function getWorkspaceName(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(MONITOR_WORKSPACE_KEY);
}

/** Full origin (no `/api/v1`), e.g. `https://host` — for building example URLs. */
function apiOrigin(): string {
  // `apiBaseUrl()` returns `<origin>/api/v1`. Strip the suffix for the origin.
  return apiBaseUrl().replace(/\/api\/v1$/, '');
}

/** Absolute URL for a contract path (which is given without the origin). */
export function monitorUrl(path: string): string {
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${apiOrigin()}${p}`;
}

/** Thrown by the helpers below with a client-friendly message. */
export class MonitorError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'MonitorError';
    this.status = status;
  }
}

async function readError(resp: Response): Promise<MonitorError> {
  let detail = `Request failed (${resp.status}).`;
  try {
    const body = (await resp.json()) as { detail?: string; title?: string };
    detail = body.detail ?? body.title ?? detail;
  } catch {
    /* keep default */
  }
  return new MonitorError(resp.status, detail);
}

/** Unauthenticated signup. */
export async function signup(name: string): Promise<{
  client_id: string;
  api_token: string;
  ingest_url: string;
}> {
  const resp = await fetch(monitorUrl('/api/v1/client/signup'), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  if (!resp.ok) throw await readError(resp);
  return resp.json();
}

/** A single signed insight the client reads, plus its public verify link. */
export interface MonitorInsight {
  severity: string;
  title: string;
  message: string;
  verdict_id: string;
  created: string;
  verify_url: string;
}

export interface MonitorInsights {
  summary: { critical: number; warning: number; ok: number };
  insights: MonitorInsight[];
}

/** The client's own signed insights. Requires a saved token. */
export async function fetchInsights(token: string): Promise<MonitorInsights> {
  const resp = await fetch(monitorUrl('/api/v1/client/monitor/insights'), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) throw await readError(resp);
  return resp.json();
}

/** Result returned after the client sends a batch of their data. */
export interface IngestResult {
  normalized: number;
  stored: number;
  verdict_id: string;
  sequence: number;
  severity: string;
  insight: string;
}

/** Submit a batch of the client's data (OTLP JSON). Requires a saved token. */
export async function ingest(token: string, otlpJson: unknown): Promise<IngestResult> {
  const resp = await fetch(monitorUrl('/api/v1/client/monitor/ingest'), {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(otlpJson),
  });
  if (!resp.ok) throw await readError(resp);
  return resp.json();
}

/** Re-verify a signed result over its public link. Returns `true` if intact. */
export async function verifyResult(verifyUrl: string): Promise<boolean> {
  // `verify_url` may arrive absolute (from the server) or as a contract path.
  const url = /^https?:\/\//i.test(verifyUrl) ? verifyUrl : monitorUrl(verifyUrl);
  const resp = await fetch(url);
  if (!resp.ok) throw await readError(resp);
  const body = (await resp.json()) as { valid?: boolean };
  return body.valid === true;
}

// ── Alerts (business Monitor — get notified on a critical finding) ────────────

/** One channel's availability (whether its transport is configured server-side). */
export interface AlertChannelStatus {
  kind: string;
  configured: boolean;
}
/** A saved per-channel preference (where + whether to alert). */
export interface AlertPref {
  kind: string;
  address: string;
  enabled: boolean;
}
export interface AlertChannels {
  available: AlertChannelStatus[];
  prefs: AlertPref[];
}

/** Read the client's alert channels + saved prefs. */
export async function getAlertChannels(token: string): Promise<AlertChannels> {
  const resp = await fetch(monitorUrl('/api/v1/me/alerts/channels'), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) throw await readError(resp);
  return resp.json();
}

/** Set one alert channel (e.g. telegram → chat id, email → address). */
export async function setAlertChannel(
  token: string,
  channel: string,
  address: string,
  enabled: boolean,
): Promise<AlertChannels> {
  const resp = await fetch(monitorUrl('/api/v1/me/alerts/channels'), {
    method: 'POST',
    headers: { 'content-type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ channel, address, enabled }),
  });
  if (!resp.ok) throw await readError(resp);
  return resp.json();
}

/** One delivery outcome from a test alert. */
export interface TestDelivery {
  channel: string;
  status: string;
  detail: string;
}
/** Fire a test alert to the client's enabled channels. */
export async function testAlert(
  token: string,
): Promise<{ deliveries: TestDelivery[]; message: string }> {
  const resp = await fetch(monitorUrl('/api/v1/me/alerts/test'), {
    method: 'POST',
    headers: { 'content-type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({}),
  });
  if (!resp.ok) throw await readError(resp);
  return resp.json();
}

// ── Personal privacy product (`/app/*`) ──────────────────────────────────────
//
// The free consumer app. Same per-client bearer transport as above, talking to
// the `/api/v1/me/privacy/*` contract. A "tracker" is something that touched
// the user's device/data (e.g. Instagram → your microphone).

/** One tracker the user can see and (try to) block. */
export interface PrivacyTracker {
  tracker_name: string;
  category: string;
  accessed_what: string;
  count: number;
  blocked: boolean;
  last_seen: string;
  evidence_id: string;
  verify_url: string;
}

/** The privacy dashboard: headline counts + the tracker list. */
export interface PrivacyDashboard {
  detected: number;
  blocked: number;
  active: number;
  trackers: PrivacyTracker[];
}

/** One observation the app reports (used by the "Scan now" demo seed). */
export interface PrivacyObservation {
  tracker_name: string;
  category: string;
  accessed_what: string;
  count: number;
}

/** Read the user's privacy dashboard. Requires a saved token. */
export async function fetchPrivacyDashboard(token: string): Promise<PrivacyDashboard> {
  const resp = await fetch(monitorUrl('/api/v1/me/privacy/dashboard'), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) throw await readError(resp);
  return resp.json();
}

/** Report observed trackers (the "Scan now" seed). Requires a saved token. */
export async function reportPrivacy(
  token: string,
  observations: PrivacyObservation[],
): Promise<{ recorded: number; evidence_id: string }> {
  const resp = await fetch(monitorUrl('/api/v1/me/privacy/report'), {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ observations }),
  });
  if (!resp.ok) throw await readError(resp);
  return resp.json();
}

/** Result of asking to block a tracker. */
export interface BlockResult {
  blocked: boolean;
  enforcement?: string;
  reason?: string;
  options?: { per_block_usd: number; unlimited_monthly_usd: number };
}

/** Ask to block a tracker by name. Requires a saved token. */
export async function blockTracker(token: string, trackerName: string): Promise<BlockResult> {
  const resp = await fetch(monitorUrl('/api/v1/me/privacy/block'), {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ tracker_name: trackerName }),
  });
  if (!resp.ok) throw await readError(resp);
  return resp.json();
}

// ── Personal Money Saver product (`/app/money`) ──────────────────────────────
//
// Same per-client bearer transport as the privacy product, talking to the
// `/api/v1/me/money/*` contract. All amounts are i64 cents; the UI renders ₪.
// There is no live bank connector — `reportMoney` (the demo seed / a future
// connector) is what feeds the analysis; "connect your bank" stays "coming".

/** One transaction the app reports (used by the "See where your money goes" seed). */
export interface MoneyTransaction {
  merchant: string;
  category: string;
  amount_cents: number;
  occurred_iso: string;
  recurring?: boolean;
}

/** A friendly, concrete savings insight ready to render. */
export interface MoneyInsight {
  title: string;
  message: string;
  saving_cents: number;
}

/** Insights payload: headline numbers + the insight list. */
export interface MoneyInsights {
  summary: { total_spent_cents: number; wasted_cents: number };
  insights: MoneyInsight[];
}

/** Read the user's money insights. Requires a saved token. */
export async function fetchMoneyInsights(token: string): Promise<MoneyInsights> {
  const resp = await fetch(monitorUrl('/api/v1/me/money/insights'), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) throw await readError(resp);
  return resp.json();
}

/** Report transactions (the "See where your money goes" seed). Requires a token. */
export async function reportMoney(
  token: string,
  transactions: MoneyTransaction[],
): Promise<{ recorded: number }> {
  const resp = await fetch(monitorUrl('/api/v1/me/money/report'), {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ transactions }),
  });
  if (!resp.ok) throw await readError(resp);
  return resp.json();
}

// ── Personal Smart Reminders product (`/app/reminders`) ──────────────────────
//
// Same per-client bearer transport as the privacy / money products, talking to
// the `/api/v1/me/reminders/*` contract. There is no live calendar / contacts
// connector — `reportReminders` (the sample seed / a future connector) is what
// feeds the nudges; "connect your calendar / contacts" stays "coming soon".

/** One reminder the app reports (used by the "Show me my nudges" seed). */
export interface NewReminder {
  kind: 'birthday' | 'reconnect' | 'renewal' | 'custom';
  title: string;
  detail: string;
  due_iso: string;
}

/** One upcoming nudge, with a friendly "days until due". */
export interface UpcomingReminder {
  id: string;
  kind: string;
  title: string;
  detail: string;
  due_iso: string;
  days_until: number;
}

/** Read the user's upcoming reminders, soonest first. Requires a saved token. */
export async function fetchUpcomingReminders(token: string): Promise<UpcomingReminder[]> {
  const resp = await fetch(monitorUrl('/api/v1/me/reminders/upcoming'), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) throw await readError(resp);
  const body = (await resp.json()) as { reminders?: UpcomingReminder[] };
  return body.reminders ?? [];
}

/** Report reminders (the "Show me my nudges" seed). Requires a saved token. */
export async function reportReminders(
  token: string,
  reminders: NewReminder[],
): Promise<{ added: number }> {
  const resp = await fetch(monitorUrl('/api/v1/me/reminders/report'), {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ reminders }),
  });
  if (!resp.ok) throw await readError(resp);
  return resp.json();
}

/** Mark a reminder done or dismissed. Requires a saved token. */
export async function actOnReminder(
  token: string,
  id: string,
  action: 'done' | 'dismiss',
): Promise<{ ok: boolean }> {
  const resp = await fetch(monitorUrl(`/api/v1/me/reminders/${id}/${action}`), {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) throw await readError(resp);
  return resp.json();
}
